import React, { useRef, useState, useEffect } from "react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useNavigate, useLocation } from "react-router-dom";
import useGlobalParams from "../../hooks/useGlobalParams";
import useSPQS from "../../hooks/useSPQS";
import useSPQSUndo from "../../hooks/useSPQSUndo";
import "./QSSP.css";
import QSSPGeneral from "./QSSPGeneral";
import QSSPPayment from "./QSSPPayment";
import { ignoreEnter } from "./utils/inputhandlers";
import QSSPPackaging from "./QSSPPackaging";
import QSSPInCharge from "./QSSPInCharge";
import QSSPDelivery from "./QSSPDelivery";
import QSSPFigures from "./QSSPFigures";
import { resetCustoms, resetInbound, resetOceanfreight, resetOutbound } from "./utils/resetFunctions";
import QSSPEcon from "./QSSPEcon";
import useCalculationsSP from "./hooks/useCalculationsSP";
import * as Calculations from "./utils/calculations";
import { undoLastEdit, redoLastEdit } from "./utils/undo";
import { saveNewSPQS, saveEditsSP } from "./api/APICalls";
import { gsap } from "gsap";
import ListWithFiltersQS from "../ListWithFiltersQS/ListWithFiltersQS";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAnglesUp, faAnglesDown, faPlusSquare, faMinusSquare } from "@fortawesome/free-solid-svg-icons";
import useLoadQSs from "./hooks/useLoadQSs";
import useLoadSPQSToEdit from "./hooks/useLoadSPQSToEdit";
import { offerToClipboard } from "./utils/offergenerators";

const QSSP = () => {
  // General
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();
  // Load Context Values
  const { globalparams } = useGlobalParams();
  const {
    setQstoeditSP,
    qstoeditSP,
    SPQS,
    SPQSData,
    setSPQS,
    setSPQSData,
    SPQSInit,
    SPQSDataInit,
    ppaytermInitSPQS,
    ppaytermDataInitSPQS,
    spaytermInitSPQS,
    spaytermDataInitSPQS,
    setPpaymenttermSPQS,
    ppaymenttermSPQS,
    setPpaymenttermDataSPQS,
    ppaymenttermDataSPQS,
    ppayrowSPQS,
    setSpaymenttermSPQS,
    spaymenttermSPQS,
    setSpaymenttermDataSPQS,
    spaymenttermDataSPQS,
    spayrowSPQS,
  } = useSPQS();
  const {
    SPQSEdits,
    editIDSPQS,
    setEditIDSPQS,
    undoListSPQS,
    showcalculatorSPQS,
    setShowcalculatorSPQS,
    undoFocusFlagSPQS,
    setUndoFocusFlagSPQS,
    inputRefsSPQS,
  } = useSPQSUndo();

  // Refs
  const QSSPsearchRef = useRef();
  const SQSMRef = useRef();
  const loadedQS = useRef(SPQS);
  const loadedQSData = useRef(SPQSData);
  const loadedPpayterm = useRef(structuredClone(ppaytermInitSPQS));
  const loadedPpaytermData = useRef(structuredClone(ppaytermDataInitSPQS));
  const loadedSpayterm = useRef(structuredClone(spaytermInitSPQS));
  const loadedSpaytermData = useRef(structuredClone(spaytermDataInitSPQS));

  // States
  const [SQSM, setSQSM] = useState(); //Save/Success/Failure Message
  const [showSQSM, setShowSQSM] = useState(false); // Flag to show SPM
  const [toggleClearSF, setToggleClearSF] = useState(false); //SearchField Clear
  const [navpostoedit, setNavpostoedit] = useState(); // passed to QSUSAGeneral to set the loaded position
  const [showposList, setShowposList] = useState(false); // flag to hide/show poslist in QS
  const [reloadPoslistdata, setReloadPoslistdata] = useState(false); // flag to reload position list data
  const [showinfo, setShowinfo] = useState(); // Show Incoterm Info
  const [loaded, setLoaded] = useState(false); // Flag to know when the position data is loaded
  const [updateLoadedPayterms, setUpdatedLoadedPayterms] = useState(false); // Flag to know when the payterms are loaded
  const [clearSelectedRowID, setClearSelectedRowID] = useState(); // clear selected row in List with Filters
  const [reloadlistdata, setReloadlistdata] = useState(); // Flag to reload list data
  const [showQSSPSearch, setShowQSSPSearch] = useState(false); // Flag to show/hide QS list
  const [limit, setLimit] = useState(300); // sets row limit to load in List with Filters

  // Set Insurance Variables when Incoterm changes
  useEffect(() => {
    SPQS && Calculations.setInsuranceVars(SPQS.sincoterm, globalparams, setSPQS, setSPQSData);
  }, [SPQS.sincoterm]);

  //Calculations Hook
  useCalculationsSP();

  // Load List Data
  const listdata = useLoadQSs(axiosPrivate, reloadlistdata, navigate, location, "loadSPQSList", limit);

  // Store loaded position to compare edits against
  useEffect(() => {
    loadedQS.current = SPQS;
    loadedQSData.current = SPQSData;
  }, [loaded]);

  // Update paymentterm and paymenttermdata after loading to include a row value
  useEffect(() => {
    setPpaymenttermSPQS((prev) =>
      prev.map((item) => {
        return { ...item, ["row"]: item.QSPaytermID || ppayrowSPQS.current };
      })
    );
    setPpaymenttermDataSPQS((prev) =>
      prev.map((item) => {
        return { ...item, ["row"]: item.QSPaytermID || ppayrowSPQS.current };
      })
    );
    setSpaymenttermSPQS((prev) =>
      prev.map((item) => {
        return { ...item, ["row"]: item.QSPaytermID || spayrowSPQS.current };
      })
    );
    setSpaymenttermDataSPQS((prev) =>
      prev.map((item) => {
        return { ...item, ["row"]: item.QSPaytermID || spayrowSPQS.current };
      })
    );
    return setUpdatedLoadedPayterms((prev) => !prev);
  }, [loaded]);

  // Store loaded payment terms to compare edits against. Do this after update above.
  useEffect(() => {
    loadedPpayterm.current = structuredClone(ppaymenttermSPQS);
    loadedPpaytermData.current = structuredClone(ppaymenttermDataSPQS);
    ppayrowSPQS.current = 0;
    loadedSpayterm.current = structuredClone(spaymenttermSPQS);
    loadedSpaytermData.current = structuredClone(spaymenttermDataSPQS);
    spayrowSPQS.current = 0;
  }, [updateLoadedPayterms]);

  // Give focus to the undo input field using inputRefs
  useEffect(() => {
    if (undoFocusFlagSPQS && inputRefsSPQS.current[undoFocusFlagSPQS]) {
      inputRefsSPQS.current[undoFocusFlagSPQS].focus();
      setToggleClearSF((prev) => !prev);
    }
    return setUndoFocusFlagSPQS("");
  }, [undoFocusFlagSPQS]);

  // Attach Undo and Redo functions with addEventListener
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for MetaKey+Z (Undo)
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && !e.shiftKey) {
        undoLastEdit(
          e,
          setToggleClearSF,
          SPQSEdits,
          undoListSPQS,
          setSPQS,
          setSPQSData,
          setShowcalculatorSPQS,
          setUndoFocusFlagSPQS,
          loadedQS,
          loadedQSData,
          setEditIDSPQS,
          setPpaymenttermSPQS,
          setPpaymenttermDataSPQS,
          ppaymenttermSPQS,
          ppaymenttermDataSPQS,
          loadedPpayterm,
          loadedPpaytermData,
          setSpaymenttermSPQS,
          setSpaymenttermDataSPQS,
          spaymenttermSPQS,
          spaymenttermDataSPQS,
          loadedSpayterm,
          loadedSpaytermData,
          setNavpostoedit
        );
      }
      // Check for MetaKey+Shift+Z (Redo)
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && e.shiftKey) {
        redoLastEdit(
          e,
          setToggleClearSF,
          undoListSPQS,
          SPQSEdits,
          setSPQS,
          setSPQSData,
          setShowcalculatorSPQS,
          setUndoFocusFlagSPQS,
          setEditIDSPQS,
          setPpaymenttermSPQS,
          setPpaymenttermDataSPQS,
          loadedPpayterm,
          loadedPpaytermData,
          setSpaymenttermSPQS,
          setSpaymenttermDataSPQS,
          loadedSpayterm,
          loadedSpaytermData,
          setNavpostoedit
        );
      }
    };
    // Add the event listener
    window.addEventListener("keydown", handleKeyDown);
    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Show Saved Position Message
  const onSPMComplete = () => {
    setSQSM(null);
  };
  useEffect(() => {
    const elem = SQSMRef.current;
    if (SQSM) {
      gsap.fromTo(elem, { opacity: 0.5, top: "-50px" }, { opacity: 1, top: 0, duration: 0.1, ease: "power2.in" });
      gsap.fromTo(
        elem,
        { opacity: 1 },
        {
          opacity: 0,
          duration: 3,
          ease: "power2.inOut",
          onComplete: onSPMComplete,
        }
      );
    }
  }, [showSQSM]);

  // Show/Hide Position List Component
  useEffect(() => {
    if (showQSSPSearch) {
      const elem = QSSPsearchRef.current;
      gsap.to(elem, {
        bottom: "-1vh",
        duration: 0.1,
        ease: "power1.inOut",
      });
    }
    if (!showQSSPSearch) {
      const elem = QSSPsearchRef.current;
      gsap.to(elem, {
        bottom: "-73vh",
        duration: 0.1,
        ease: "power1.inOut",
      });
    }
  }, [showQSSPSearch]);

  // Clear QS Function
  const clearSPQS = () => {
    setSPQS(SPQSInit);
    setSPQSData(SPQSDataInit);
    setPpaymenttermSPQS(ppaytermInitSPQS);
    setPpaymenttermDataSPQS(ppaytermDataInitSPQS);
    setSPQSData(SPQSDataInit);
    setSpaymenttermSPQS(spaytermInitSPQS);
    setSpaymenttermDataSPQS(spaytermDataInitSPQS);
    setQstoeditSP(null);
    setClearSelectedRowID(false);
    setLoaded((prev) => !prev);
    setUpdatedLoadedPayterms((prev) => !prev);
    setToggleClearSF((prev) => !toggleClearSF);
    setEditIDSPQS(0);
  };

  // Select Position to Edit
  const selectQSToEdit = (item) => {
    clearSPQS();
    setQstoeditSP(item.SPQSID);
    setShowQSSPSearch(false);
  };

  // Load Position to Edit
  useLoadSPQSToEdit(
    axiosPrivate,
    qstoeditSP,
    setSPQS,
    setSPQSData,
    setPpaymenttermSPQS,
    setPpaymenttermDataSPQS,
    ppaytermInitSPQS,
    ppaytermDataInitSPQS,
    setLoaded,
    navigate,
    location,
    setSpaymenttermSPQS,
    setSpaymenttermDataSPQS,
    spaytermInitSPQS,
    spaytermDataInitSPQS,
    setShowSQSM,
    setSQSM,
    setNavpostoedit
  );

  return (
    <div className='addQSSP-container'>
      <form
        className='addQSSP'
        onSubmit={(e) => {
          e.preventDefault();
          if (qstoeditSP) {
            saveEditsSP(
              SPQSEdits,
              axiosPrivate,
              qstoeditSP,
              setReloadlistdata,
              setSQSM,
              setShowSQSM,
              ppaymenttermDataSPQS,
              spaymenttermDataSPQS,
              setReloadPoslistdata
            );
          } else {
            saveNewSPQS(
              axiosPrivate,
              SPQSData,
              ppaymenttermDataSPQS,
              setReloadlistdata,
              reloadlistdata,
              setSQSM,
              setShowSQSM,
              showSQSM,
              spaymenttermDataSPQS,
              clearSPQS,
              setReloadPoslistdata
            );
          }
        }}
        onClick={(e) => {
          setShowQSSPSearch(false);
          if (showposList) setShowposList(false);
        }}
      >
        <div className='addQSSP-header'>
          <h1>{qstoeditSP ? "Edit Spain QS:" : "Add New Spain QS:"}</h1>
          <div className='SPQS-success-msg' ref={SQSMRef}>
            {SQSM}
          </div>
          <div className='form-buttons'>
            <button
              type='button'
              tabIndex={201}
              onClick={(e) => {
                clearSPQS();
              }}
            >
              Clear
            </button>
            {!qstoeditSP && (
              <button className='mainbutton' type='submit' tabIndex={201}>
                Save New
              </button>
            )}
            {qstoeditSP && (
              <button
                className={SPQSEdits.current.length > 0 ? "mainbutton" : "mainbutton btndisabled"}
                type='submit'
                tabIndex={201}
              >
                Save Edits
              </button>
            )}
          </div>
        </div>
        <div className='addQSSP-formcontainer'>
          <div className='addQSSP-1'>
            <QSSPGeneral
              clearfield={toggleClearSF}
              setClearfield={setToggleClearSF}
              navpostoedit={navpostoedit}
              setNavpostoedit={setNavpostoedit}
              showUSposList={showposList}
              setShowUSposList={setShowposList}
              setReloadPoslistdata={setReloadPoslistdata}
              reloadPoslistdata={reloadPoslistdata}
            />
            <QSSPPayment
              loadedPpayterm={loadedPpayterm}
              loadedPpaytermData={loadedPpaytermData}
              loadedSpayterm={loadedSpayterm}
              loadedSpaytermData={loadedSpaytermData}
              ignoreEnter={(e) => ignoreEnter(e, setShowcalculatorSPQS, showcalculatorSPQS)}
              clearfield={toggleClearSF}
            />
          </div>
          <div className='addposSP-2' style={{ overflow: "visible" }}>
            <QSSPInCharge clearfield={toggleClearSF} />
            <QSSPPackaging clearfield={toggleClearSF} />
            <QSSPDelivery clearfield={toggleClearSF} setShowinfo={setShowinfo} showinfo={showinfo} />
          </div>
          <div className='addQSSP-3'>
            <div className='addQSSP-31'>
              <QSSPFigures
                ignoreEnter={(e) => ignoreEnter(e, setShowcalculatorSPQS, showcalculatorSPQS)}
                setShowcalculator={setShowcalculatorSPQS}
                showcalculator={showcalculatorSPQS}
                resetOutbound={(e) =>
                  resetOutbound(
                    SPQS,
                    SPQSData,
                    setSPQS,
                    setSPQSData,
                    SPQSInit,
                    SPQSDataInit,
                    SPQSEdits,
                    editIDSPQS,
                    setEditIDSPQS
                  )
                }
                resetOceanfreight={(e) => resetOceanfreight(setSPQS, setSPQSData, SPQSInit, SPQSDataInit)}
                resetCustoms={(e) => resetCustoms(setSPQS, setSPQSData, SPQSInit, SPQSDataInit)}
                resetInbound={(e) => resetInbound(setSPQS, setSPQSData, SPQSInit, SPQSDataInit)}
                clearfield={toggleClearSF}
              />
            </div>
            <div className='addQSSP-32'>
              <QSSPEcon clearfield={toggleClearSF} />
              <button
                id='copyOfferBtn'
                type='button'
                onClick={() => offerToClipboard(SPQS, spaymenttermSPQS, setSQSM, setShowSQSM)}
              >
                Copy Offer to Clipboard
              </button>
              <div className='SPQSsoldgroup'>
                <input
                  type='checkbox'
                  checked={SPQS.sold === 1}
                  onChange={(e) => {
                    const newValue = e.target.checked ? 1 : 0;
                    setSPQS((prev) => ({ ...prev, sold: newValue }));
                    setSPQSData((prev) => ({ ...prev, sold: newValue }));
                    SPQSEdits.current.push({
                      editID: editIDSPQS,
                      name: "sold",
                      value: newValue,
                      num: newValue,
                      figuretype: "radio",
                    });
                    setEditIDSPQS((prev) => prev + 1);
                    undoListSPQS.current = [];
                  }}
                />
                <label htmlFor=''>Sold:</label>
              </div>
            </div>
          </div>
        </div>
      </form>
      <div className='QSsearchSP' ref={QSSPsearchRef} tabIndex={-1}>
        <div className='QSsearchSP-header' onClick={(e) => setShowQSSPSearch(!showQSSPSearch)}>
          {!showQSSPSearch ? <FontAwesomeIcon icon={faAnglesUp} /> : <FontAwesomeIcon icon={faAnglesDown} />}
        </div>
        <div style={{ marginTop: "1rem" }}>
          <ListWithFiltersQS
            sortable={true}
            sortDirections={[
              "NON",
              "DESC",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
              "NON",
            ]}
            data={listdata}
            columns={[
              "QSDate",
              "SPQSID",
              "QSType",
              "WGP",
              "WGS",
              "product",
              "supplier",
              "customer",
              "shipfrom",
              "shipuntil",
              "POL",
              "POD",
              "quantity",
              "cost",
              "freight",
              "salesprice",
              "profit",
              "totalprofit",
              "margin",
              "sold",
            ]}
            size={["M", "S", "S", "S", "S", "M", "M", "M", "S", "S", "M", "M", "S", "S", "S", "S", "S", "M", "S", "M"]}
            rowID={"SPQSID"}
            placeHolders={true}
            placeHolderList={[
              "QSDate",
              "SPQSID",
              "QSType",
              "WGP",
              "WGS",
              "Product",
              "Supplier",
              "Customer",
              "From",
              "To",
              "POL",
              "POD",
              "Quantity",
              "Cost",
              "Freight",
              "Price",
              "Profit",
              "Total Profit",
              "Margin",
              "Sold",
            ]}
            limit={limit}
            setLimit={setLimit}
            setReloadlistdata={setReloadlistdata}
            onRowClick={selectQSToEdit}
            setClearSelectedRowID={setClearSelectedRowID}
            clearSelectedRowID={clearSelectedRowID}
            duplicateurl='duplicateSPQS'
            tabIndex={-1}
          />
        </div>
      </div>
    </div>
  );
};

export default QSSP;
